import React, { useEffect } from 'react';
import { Typography, Box, Button } from '@mui/material';
import { SignInData } from 'Types';
import { CustomTextField, CustomButton } from 'Components/StyledComponents';
import { FormikProps } from 'formik';
import { isPhoneNumberWithCountryCode, validateEmailOrPhone } from './SignUpForm';
import * as yup from 'yup';
import CountryCodeSelect from './CountryCodeSelect';
import { getLastLoginMethod } from 'Utils/LastLoginMethod';
import LastUsedBadge from 'Components/LastUsedBadge';
import { touchTargetHeightSx } from 'Utils/touchTarget';

export type SignInFormProps = {
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  forgotPasswordNavFunction: () => void;
} & FormikProps<SignInData>;

// eslint-disable-next-line react-refresh/only-export-components -- shared schema
export const signInFormSchema = yup.object().shape({
  emailOrPhone: yup
    .string()
    .test('emailOrPhoneNumber', 'Must be an email or a phone number', validateEmailOrPhone)
    .required('Email or Phone Number is a required field'),
  password: yup.string().required('Password is a required field'),
  countryacronym: yup.string(),
});

const SignInForm: React.FC<SignInFormProps> = (props: SignInFormProps) => {
  const { handleChange, values, errors, forgotPasswordNavFunction } = props;
  const [isPhoneNumber, setIsPhoneNumber] = React.useState(false);
  const [emailFocused, setEmailFocused] = React.useState(false);
  // Badge the email/password method if it was the last one used on this device.
  // The chip rides the email field, and only in the empty/unfocused state, so it
  // never sits on top of what the user is typing.
  const lastLoginMethod = React.useMemo(() => getLastLoginMethod(), []);
  const showEmailBadge = lastLoginMethod === 'email' && !emailFocused && !values.emailOrPhone;

  useEffect(() => {
    if (isPhoneNumberWithCountryCode(values.emailOrPhone)) {
      setIsPhoneNumber(true);
    } else {
      setIsPhoneNumber(false);
    }
  }, [values.emailOrPhone]);

  return (
    <Box>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {isPhoneNumber && (
          <CountryCodeSelect handleChange={handleChange} countryAcronym={values.countryAcronym} />
        )}
        <CustomTextField
          sx={{ flex: 1, marginBottom: '20px' }}
          onChange={handleChange}
          id="emailOrPhone"
          name="emailOrPhone"
          type={isPhoneNumber ? 'tel' : 'email'}
          inputMode={isPhoneNumber ? 'tel' : 'email'}
          // This value is the account identifier even when it is an email or
          // phone number. Password managers use `username` to pair it with the
          // current-password field below.
          autoComplete="username"
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
          inputProps={{
            autoCapitalize: 'none',
            autoCorrect: 'off',
            spellCheck: false,
          }}
          InputProps={{
            endAdornment: showEmailBadge ? <LastUsedBadge variant="chip" /> : undefined,
          }}
          label="Email or Phone Number"
          value={values.emailOrPhone || ''}
          error={!!errors.emailOrPhone}
          helperText={errors.emailOrPhone}
          fullWidth
        />
      </Box>
      <CustomTextField
        sx={{ width: '100%', marginBottom: '0px' }}
        onChange={handleChange}
        id="password"
        name="password"
        type="password"
        autoComplete="current-password"
        inputProps={{
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: false,
        }}
        label="Password"
        value={values.password || ''}
        error={!!errors.password}
        helperText={errors.password}
        fullWidth
      />

      <Box>
        {errors.unknown && (
          <Typography color="error" variant="h6">
            {errors.unknown}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          textAlign: 'end',
          lineHeight: 0,
          marginTop: '0px',
        }}
      >
        <Button
          variant="text"
          onClick={() => {
            forgotPasswordNavFunction();
          }}
          sx={{
            color: '#CED0D4',
            textAlign: 'center',
            cursor: 'pointer',
            textDecoration: 'underline',
            ...touchTargetHeightSx,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Forgot Password?
        </Button>
      </Box>

      <Box sx={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
        <CustomButton variant="contained" type="submit">
          SIGN IN
        </CustomButton>
      </Box>
    </Box>
  );
};

export default SignInForm;
